import { getProctoringOptionDisabledDetails, isOptionSupportedForPlatform } from "@verity/proctoring-option";
import {
	Application,
	Extension,
	OptionRelatedInstituteSettings,
	ProctoringOption,
	ProctoringPlatforms,
} from "@verity/proctoring-option/types";
import type { CoreFileItem } from "../../core";
import { EditQuizType, VerityInstituteSettings } from "../../types/common";
import { checkIfVerityAssessment, checkIfStandaloneAssessment } from "../editQuizHelper";
import {
	PROCTORING_OPTION_TEXT,
	ProctoringOptionCategory,
} from "../proctoring-options/option-text/ProctoringOptionText";
import i18nPackage from "../../i18n/i18n";
import { ProctoringDataType } from "../hooks/state/useSettingsState";
import {
	GroupContext,
	isBypassStrategy,
	OPTION_STATUS_MAP,
	STATUS_SORT_WEIGHT,
	SUMMARY_GROUPS,
	SummaryItem,
	SummarySubOption,
} from "./groupProcessors";

export function parseApplication(rawStr: string, applications: Application[]): Application {
	const matchedApp = applications.find((app) => {
		return app.processName.includes(rawStr);
	});

	const matchedAppName = matchedApp ? matchedApp.name : rawStr;
	const matchedAppIcon = matchedApp ? matchedApp.iconSvg : undefined;

	return {
		name: matchedAppName,
		processName: matchedApp?.processName ?? [rawStr],
		type: matchedApp?.type ?? "Other",
		iconSvg: matchedAppIcon,
	};
}

export function getUniqueApplicationNamesAndIcons(
	applicationNames: string[],
	applications: Application[],
): Record<string, string | undefined> {
	const matchedApps: Record<string, string | undefined> = {};
	const uniqueApps = new Set<string>();

	applicationNames.forEach((appName) => {
		const matchedApp = parseApplication(appName, applications);
		const matchedAppName = matchedApp.name;
		const matchedAppIcon = matchedApp.iconSvg;

		if (!uniqueApps.has(matchedAppName)) {
			uniqueApps.add(matchedAppName);
			matchedApps[matchedAppName] = matchedAppIcon;
		}
	});

	return matchedApps;
}

export function parseExtension(rawStr: string, extensions: Extension[]): Extension {
	const id = rawStr.slice(-32);
	const fallbackName = rawStr.slice(0, -32);

	// Try to find preset extension profile by ID
	const preset = extensions.find((ext) => ext.id === id);
	const finalName = preset ? preset.name : fallbackName;
	const svgIcon = preset ? preset.iconSvg : undefined;

	return {
		id,
		name: finalName,
		type: preset?.type ?? "Other",
		iconSvg: svgIcon,
	};
}

export function filterOptionsByPlatform(
	option: Partial<ProctoringOption>,
	proctoringPlatforms?: (keyof ProctoringPlatforms)[],
): (keyof ProctoringOption)[] {
	const finalProctoringPlatforms = proctoringPlatforms ?? getActiveProctoringPlatforms(option);
	const filteredOptions: (keyof ProctoringOption)[] = [];
	const excludedKeys = new Set<keyof ProctoringOption>([
		"isExtension",
		"isLockdown",
		"isMobile",
		"isChromeOs",
		"isBrowser",
		"showRecordingPanel",
	]);

	// Iterate over entries and ensure keys are treated as keyof ProctoringOption
	for (const [key, value] of Object.entries(option) as [
		keyof ProctoringOption,
		(typeof option)[keyof ProctoringOption],
	][]) {
		if (excludedKeys.has(key)) {
			// Skip the excluded keys
			continue;
		}

		if (!finalProctoringPlatforms.some((platform) => isOptionSupportedForPlatform(platform, key, value))) {
			continue;
		}

		if (typeof value === "boolean" && value) {
			// If the value is boolean and true, add the key to the list
			filteredOptions.push(key);
		} else if (typeof value === "string" && value !== "off") {
			// If the value is a string and not 'off', add the key to the list
			filteredOptions.push(key);
		} else if (key === "flaggedKeywords" && value != null) {
			filteredOptions.push(key);
		} else if (Array.isArray(value) && value.length > 0) {
			// If the value is an array and not empty, add the key to the list
			filteredOptions.push(key);
		}
	}

	return filteredOptions;
}

function getActiveProctoringPlatforms(
	option: Partial<ProctoringOption>,
	verityInstituteSettings?: VerityInstituteSettings,
): (keyof ProctoringPlatforms)[] {
	const proctoringPlatformsKey: (keyof ProctoringPlatforms)[] = [];

	if (option.isExtension) {
		if (!verityInstituteSettings || verityInstituteSettings.allowExtensionPlatform) {
			proctoringPlatformsKey.push("isExtension");
		}
	}
	if (option.isLockdown) {
		if (!verityInstituteSettings || verityInstituteSettings.allowLockdownPlatform) {
			proctoringPlatformsKey.push("isLockdown");
		}
	}
	if (option.isMobile) {
		if (!verityInstituteSettings || verityInstituteSettings.allowMobilePlatform) {
			proctoringPlatformsKey.push("isMobile");
		}
	}

	if (option.isChromeOs) {
		proctoringPlatformsKey.push("isChromeOs");
	}
	if (option.isBrowser) {
		if (!verityInstituteSettings || verityInstituteSettings.allowBrowserPlatform) {
			proctoringPlatformsKey.push("isBrowser");
		}
	}

	return proctoringPlatformsKey;
}

export type CategorizeProctoringOptionsParams = {
	option: Partial<ProctoringOption>;
	verityInstituteSettings?: VerityInstituteSettings;
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
	applications?: ProctoringDataType["applications"];
	extensions?: ProctoringDataType["extensions"];
	quizType?: EditQuizType;
	isThirdPartyEnabled?: boolean;
	/**The instructor-uploaded reference material files, if any. When present and
	 * `referenceMaterialMode` is `"instructor"`, the summary text links to the first file. */
	referenceMaterialFiles?: CoreFileItem[];
};

export function categorizeProctoringOptions({
	applications = [],
	extensions = [],
	option,
	verityInstituteSettings,
	optionRelatedInstituteSettings,
	quizType,
	isThirdPartyEnabled,
	referenceMaterialFiles,
}: CategorizeProctoringOptionsParams): Record<ProctoringOptionCategory, SummaryItem[]> {
	const proctoringPlatformsKey = getActiveProctoringPlatforms(option, verityInstituteSettings);
	const activeOptions = filterOptionsByPlatform(option, proctoringPlatformsKey);

	const summaries: Record<ProctoringOptionCategory, SummaryItem[]> = {
		record: [],
		verification: [],
		otherActivities: [],
		showTools: [],
	};

	// Strict validation check flag helper
	const getStrictModeEnabled = (optionKey: keyof ProctoringOption) =>
		verityInstituteSettings &&
		optionRelatedInstituteSettings &&
		getProctoringOptionDisabledDetails(
			optionKey,
			"strict",
			option,
			verityInstituteSettings,
			optionRelatedInstituteSettings,
		)?.allowedValues.includes("strict");

	const groupContext: GroupContext = {
		applications,
		extensions,
		option,
		verityInstituteSettings,
		optionRelatedInstituteSettings,
		quizType,
		isThirdPartyEnabled,
		activeOptions,
		getStrictModeEnabled,
	};

	const processedGroupKeys = new Set<keyof ProctoringOption>();

	// Process groups
	SUMMARY_GROUPS.forEach((group) => {
		const activeKeys = group.triggerKeys?.filter((k) => activeOptions.includes(k)) ?? [];
		if (activeKeys.length === 0) return;

		const config = group.getConfig(groupContext);

		const hasBypassActive = config.subOptions.some((sub) => {
			if (isBypassStrategy(sub.strategy)) return true;
			return sub.subOptions?.some((nested) => isBypassStrategy(nested.strategy));
		});

		// For these keys, if bypass mode is not active, render them as individual options instead of a list
		if (!hasBypassActive && config.subOptions.length === 1) {
			return;
		}

		const textConfig = PROCTORING_OPTION_TEXT[group.key];
		const targetCategory = textConfig?.category ?? "otherActivities";

		const subOptions = config.subOptions;
		if (subOptions.length > 0) {
			const header: SummarySubOption = {
				key: group.key,
				strategy: { type: "header", text: config.listHeader ?? config.mainLabel },
			};
			subOptions.unshift(header);
		}

		summaries[targetCategory].push({
			key: group.key,
			text: config.mainLabel,
			icon: group.icon ?? textConfig?.icon,
			subOptions,
			listTooltip: config.mainAriaLabel,
			status: "list",
		});

		activeKeys.forEach((k) => processedGroupKeys.add(k));
	});

	// Individual options
	activeOptions.forEach((key) => {
		if (processedGroupKeys.has(key)) return;

		const textConfig = PROCTORING_OPTION_TEXT[key];
		if (!textConfig || !textConfig.category) return;

		const category = textConfig.category;
		const targetCategory = summaries[category] ? category : "otherActivities";

		if (
			key === "enableManualEndProctoring" &&
			(!proctoringPlatformsKey.includes("isLockdown") ||
				checkIfVerityAssessment(quizType) ||
				checkIfStandaloneAssessment(quizType) ||
				isThirdPartyEnabled)
		) {
			return;
		}

		// Uses compact header first
		let mainLabel: SummaryItem["text"] = textConfig.compactHeader ?? textConfig.header;
		const mainIcon: SummaryItem["icon"] = textConfig.icon;

		// Calculator
		if (key === "enableCalculator") {
			const typeLabel =
				option.enableCalculator === "scientific"
					? i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.calculator.scientific")
					: i18nPackage.t("editQuizPage.configurationStep.showToolsTSX.calculator.standard");
			mainLabel = i18nPackage.t("editQuizPage.configurationSummaryListTSX.calculator", { type: typeLabel });
		}

		summaries[targetCategory].push({
			key: key,
			text: mainLabel,
			icon: mainIcon,
			subOptions: [],
			status: OPTION_STATUS_MAP[key] ?? "monitored",
			file: key === "referenceMaterialMode" ? referenceMaterialFiles?.[0] : undefined,
		});
	});

	summaries.otherActivities.sort((a, b) => {
		const weightA = a.status ? STATUS_SORT_WEIGHT[a.status] : 99;
		const weightB = b.status ? STATUS_SORT_WEIGHT[b.status] : 99;
		return weightA - weightB;
	});

	return summaries;
}
